import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import { useCountdown } from '@renderer/utils/countdownUtils'
import { useCachedBackgroundImage } from '@renderer/hooks/useCachedBackgroundImage'
import { formatAmount, normalizeCurrency } from '@renderer/utils/formatCurrency'

const CheckoutPage: React.FC = () => {
  const navigate = useNavigate()
  const { projectId } = useParams()
  const { setSessionState, mode, logActivity, userPreferences } = useSessionStateStore()
  const { config: globalConfig } = useConfigStore()
  const config = globalConfig?.pages.checkout
  const projectCurrency = normalizeCurrency(globalConfig?.resolvedCurrency)
  const [selectedCopies, setSelectedCopies] = useState<number>(1)
  const [remainingTime, setRemainingTime] = useState<string>('')

  // Initialize countdown with the useCountdown hook
  const checkoutTimeout = config?.settings?.checkoutTimeout || 0
  const countdown = useCountdown(checkoutTimeout)

  // Get booth mode from user preferences
  const boothMode = userPreferences.boothMode

  // Pricing function based on booth mode
  const getPricing = (copies: number): number => {
    let basePrice = 0

    switch (boothMode) {
      case 'japanese':
        basePrice = 86000
        break
      case 'photostrip':
        basePrice = 65000
        break
      case 'flipbook':
        basePrice = 127000
        break
      default:
        basePrice = 65000 // Default to photostrip pricing
    }

    // 2 copies cost 10k more than 1 copy
    return copies === 1 ? basePrice : basePrice + 10000
  }

  const handleNext = async (): Promise<void> => {
    const amount = getPricing(selectedCopies)

    const details = {
      copies: selectedCopies,
      amount: amount,
      boothMode: boothMode,
      package: `${selectedCopies} copy photo`,
      packageDescription: `${selectedCopies} copy photo`
    }

    logActivity('Checkout complete.', details)

    setSessionState({
      userPreferences: {
        copies: selectedCopies,
        usePrint: true,
        useDigital: true
      },
      transaction: {
        amount: amount
      }
    })

    navigate(`/${projectId}/payment`)
  }

  // Set up countdown callbacks
  useEffect(() => {
    if (checkoutTimeout > 0) {
      // Set the onTick callback to update the remaining time display
      countdown.setOnTick(() => {
        setRemainingTime(countdown.getFormattedRemainingSeconds())
      })

      // Set the onComplete callback
      countdown.setOnComplete(() => {
        const details = {
          selectedCopies,
          amount: getPricing(selectedCopies),
          boothMode: boothMode,
          timeoutDuration: checkoutTimeout
        }
        logActivity('Checkout timed out.', details)
        handleNext()
      })
    }
  }, [checkoutTimeout, selectedCopies, boothMode, handleNext])

  // Start the countdown on mount
  useEffect(() => {
    if (checkoutTimeout > 0) {
      countdown.start()

      return (): void => {
        countdown.stop()
      }
    }
    return
  }, [checkoutTimeout])

  // If flipbook (record mode), go directly to payment
  useEffect(() => {
    if (mode === 'record') {
      logActivity('Record mode detected, navigating directly to payment')
      navigate(`/${projectId}/payment`)
    }
  }, [mode, navigate, projectId])

  const handleCopySelect = (copies: number): void => {
    setSelectedCopies(copies)
    logActivity(`User selected ${copies} copy(ies)`)
    // Immediately continue to payment
    setTimeout(() => {
      const amount = getPricing(copies)

      const details = {
        copies: copies,
        amount: amount,
        boothMode: boothMode,
        package: `${copies} copy photo`,
        packageDescription: `${copies} copy photo`
      }

      logActivity('Checkout complete.', details)

      setSessionState({
        userPreferences: {
          copies: copies,
          usePrint: true,
          useDigital: true
        },
        transaction: {
          amount: amount
        }
      })

      navigate(`/${projectId}/payment`)
    }, 300) // Small delay for visual feedback
  }

  // Log booth mode on mount
  useEffect(() => {
    logActivity(`Checkout page loaded with booth mode: ${boothMode}`)
  }, [boothMode, logActivity])

  // If record mode, don't render this page
  if (mode === 'record') {
    return null
  }

  const cachedCheckoutBg = useCachedBackgroundImage('/assets/boothndaughter/checkout.webp')
  const cachedOneCopyBg = useCachedBackgroundImage('/assets/boothndaughter/1-copy.webp')
  const cachedTwoCopyBg = useCachedBackgroundImage('/assets/boothndaughter/2-copy.webp')

  return (
    <div
      className="w-full h-full relative overflow-hidden"
      style={{
        backgroundImage: `url(${cachedCheckoutBg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* 1 Copy Option - Left Center */}
      <div className="absolute left-1/4 top-1/2 transform -translate-x-1/2 -translate-y-1/2 flex flex-col items-center justify-center">
        <button
          onClick={() => handleCopySelect(1)}
          title="Select 1 copy"
          className="relative transition-all duration-300 transform hover:scale-105 active:scale-95 mt-64"
          style={{
            width: '500px',
            height: '500px',
            backgroundImage: `url(${cachedOneCopyBg})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            border: 'none',
            outline: 'none',
            cursor: 'pointer'
          }}
        />
        <div className="text-center mt-4 flex flex-col items-center justify-center">
          <span
            style={{
              fontFamily: 'monospace',
              fontSize: '64px',
              color: 'white',
              textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
            }}
          >
            {formatAmount(getPricing(1), projectCurrency)}
          </span>
          <span
            style={{
              fontSize: '36px',
              color: 'white',
              fontFamily: 'monospace',
              textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
            }}
          >
            (1 copy)
          </span>
        </div>
      </div>

      {/* 2 Copy Option - Right Center */}
      <div className="absolute right-1/4 top-1/2 transform translate-x-1/2 -translate-y-1/2 flex flex-col items-center justify-center">
        <button
          onClick={() => handleCopySelect(2)}
          title="Select 2 copies"
          className="relative transition-all duration-300 transform hover:scale-105 active:scale-95 mt-64"
          style={{
            width: '500px',
            height: '500px',
            backgroundImage: `url(${cachedTwoCopyBg})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            border: 'none',
            outline: 'none',
            cursor: 'pointer'
          }}
        />
        <div className="text-center mt-4 flex flex-col items-center justify-center">
          <span
            style={{
              fontFamily: 'monospace',
              fontSize: '64px',
              color: 'white',
              textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
            }}
          >
            {formatAmount(getPricing(2), projectCurrency)}
          </span>
          <span
            style={{
              fontSize: '36px',
              color: 'white',
              fontFamily: 'monospace',
              textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
            }}
          >
            (2 copy)
          </span>
        </div>
      </div>

      {/* Remaining Time Display */}
      {remainingTime && (
        <div className="absolute top-8 right-8">
          <span
            style={{
              fontFamily: 'monospace',
              fontSize: '24px',
              color: 'white',
              textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
            }}
          >
            {remainingTime}
          </span>
        </div>
      )}
    </div>
  )
}

export default CheckoutPage
